'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import type React from 'react';
import { useContext } from 'react';

import type { PredictionSide } from '@flipster/apis/v1/price-prediction';
import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { Badge } from '@flipster/react/design-system/badge';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import ArrowDownRightIcon from '@flipster/react/icon/generated/ArrowDownRightIcon';
import ArrowUpRightIcon from '@flipster/react/icon/generated/ArrowUpRightIcon';
import CheckCircleIcon from '@flipster/react/icon/generated/CheckCircleIcon';
import ClockIcon from '@flipster/react/icon/generated/ClockIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css, cva } from '@flipster/styled-system/css';
import { center, hstack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';

import { CompetitionIdContext } from '../../state';
import { btcPriceCardBunja } from './btc-price-card.state';
import { votingCardBunja } from './voting-card.state';
import { VotingOpensSoonDialog } from './voting-opens-soon-dialog';

export const VotingCard = () => {
  const id = useContext(CompetitionIdContext);
  const {
    roundInfoAtom,
    timeLeftMinutesAtom,
    upRateAtom,
    downRateAtom,
    predictionEnableAtom,
    predictionSideAtom,
    postPredictionMutationAtom,
    isRegisteredAtom,
    isUpcomingAtom,
    registerCompetition,
  } = useBunja(votingCardBunja);
  const { startingPriceAtom, priceInfoAtom } = useBunja(btcPriceCardBunja);

  const { round, totalRounds } = useAtomValue(roundInfoAtom);
  const timeLeftMinutes = useAtomValue(timeLeftMinutesAtom);
  const upRate = useAtomValue(upRateAtom);
  const downRate = useAtomValue(downRateAtom);
  const predictionEnable = useAtomValue(predictionEnableAtom);
  const predictionSide = useAtomValue(predictionSideAtom);
  const { mutate, isPending } = useAtomValue(postPredictionMutationAtom);
  const isRegistered = useAtomValue(isRegisteredAtom);
  const isUpcoming = useAtomValue(isUpcomingAtom);
  const startingPrice = useAtomValue(startingPriceAtom);
  const { currentPrice } = useAtomValue(priceInfoAtom);

  const overlay = useOverlay();

  const handleVote = (side: PredictionSide) => {
    if (isPending) return;

    if (!predictionEnable) {
      overlay.open(({ isOpen, onClose, unmount }) => (
        <VotingOpensSoonDialog isOpen={isOpen} onClose={onClose} onExit={unmount} />
      ));
      return;
    }
    segment.track(CompetitionsEvent.CompetitionPricePredictionVoteClick, {
      competition_id: id,
      vote_direction: side,
      round_number: round,
      starting_price: startingPrice ? Number(startingPrice) : null,
      current_price: currentPrice ? Number(currentPrice) : null,
      percentage_up: upRate,
      percentage_down: downRate,
      time_left_minute: timeLeftMinutes,
    });

    if (!isRegistered) {
      dialog.open({
        title: { label: <FormattedMessage id='Campaign.r260123.Register_to_vote' /> },
        content: <FormattedMessage id='Campaign.r260123.Not_registered_error' />,
        buttons: {
          direction: 'vertical',
          primary: (
            <Dialog.Button
              hierarchy='primary'
              label={<FormattedMessage id='Common.r250923.Register_now' />}
              disabled={isPending}
              onClick={async () => {
                segment.track(CompetitionsEvent.CompetitionTradingRegisterClick, {
                  competition_id: id,
                  button_location: 'popup',
                });
                await registerCompetition();
                mutate(side);
                dialog.close();
              }}
            />
          ),
          secondary: (
            <Dialog.Button
              hierarchy='secondary'
              color='neutral'
              label={<FormattedMessage id='Reward_hub.r250213.Close' />}
              onClick={() => dialog.close()}
            />
          ),
        },
      });
      return;
    }

    mutate(side);
  };

  return (
    <div
      className={vstack({
        flex: 1,
        px: { base: 16, tablet: 24 },
        py: { base: 20, tablet: 24 },
        gap: 24,
        rounded: 16,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'border.default.level-2',
        alignItems: 'stretch',
      })}
    >
      {/* Header: Round + Timer */}
      <div className={hstack({ justifyContent: 'space-between', alignItems: 'center' })}>
        <div className={hstack({ gap: 4, alignItems: 'flex-end' })}>
          <span className={css({ textStyle: 'Title/S', color: 'content.default.level-5' })}>
            <FormattedMessage id='Campaign.r260123.Round_number' values={{ number: round }} />
          </span>
          <span className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-1' })}>/{totalRounds}</span>
        </div>
        <TimerBadge timeLeftMinutes={timeLeftMinutes} isUpcoming={isUpcoming} />
      </div>

      {/* Voting Buttons */}
      <div
        className={hstack({ gap: { base: 16, tablet: 48 }, justifyContent: 'center', alignItems: 'center', flex: 1 })}
      >
        <VoteButton
          side='UP'
          rate={upRate}
          isSelected={predictionSide === 'UP'}
          disabled={!!predictionSide || isPending}
          onClick={() => handleVote('UP')}
        />
        <span
          className={css({
            textStyle: { base: 'Text/M/Strong', tablet: 'Title/S' },
            color: 'inverse.white',
          })}
        >
          VS
        </span>
        <VoteButton
          side='DOWN'
          rate={downRate}
          isSelected={predictionSide === 'DOWN'}
          disabled={!!predictionSide || isPending}
          onClick={() => handleVote('DOWN')}
        />
      </div>

      {/* Progress Bar */}
      <div className={vstack({ gap: 16, w: '100%', alignItems: 'stretch' })}>
        <div className={hstack({ w: '100%', h: 8, overflow: 'hidden' })}>
          <div
            className={css({ h: '100%', bg: 'content.state.positive', rounded: 1000 })}
            style={{ width: `${upRate}%` }}
          />
          <div
            className={css({ h: '100%', bg: 'content.state.negative', rounded: 1000 })}
            style={{ width: `${downRate}%` }}
          />
        </div>

        {/* Notice */}
        <div className={hstack({ gap: 4, alignItems: 'flex-start' })}>
          <InformationCircleIcon
            size={16}
            className={css({ color: 'content.default.level-2', mt: 2, flexShrink: 0 })}
          />
          <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
            <FormattedMessage id='Campaign.r260123.Price_prediction_timeline' />
          </p>
        </div>
      </div>
    </div>
  );
};

interface VoteButtonProps {
  side: PredictionSide;
  rate: number;
  isSelected: boolean;
  disabled: boolean;
  onClick: () => void;
}

const VoteIcon = ({ isUp, size = 20 }: { isUp: boolean; size?: number }) =>
  isUp ? (
    <ArrowUpRightIcon className={css({ color: 'content.state.positive' })} size={size} />
  ) : (
    <ArrowDownRightIcon className={css({ color: 'content.state.negative' })} size={size} />
  );

const VoteRate = ({ rate, isUp }: { rate: number; isUp: boolean; isLarge?: boolean }) => (
  <span
    className={
      isUp
        ? css({ textStyle: 'Title/L', color: 'content.state.positive' })
        : css({ textStyle: 'Title/L', color: 'content.state.negative' })
    }
  >
    {rate}%
  </span>
);

const VoteButton = ({ side, rate, isSelected, disabled, onClick }: VoteButtonProps) => {
  const isUp = side === 'UP';
  const isDisabledState = disabled && !isSelected;
  const bgColor = isUp ? 'background.state.positive.tint' : 'background.state.negative.tint';

  // 선택된 상태 (투표 완료 후)
  if (isSelected) {
    return (
      <div
        className={css({
          flex: 1,
          rounded: 16,
          borderWidth: '2px',
          borderStyle: 'solid',
          borderColor: isUp ? 'content.state.positive' : 'content.state.negative',
          overflow: 'hidden',
          pos: 'relative',
          h: { base: 157, tablet: '100%' },
        })}
      >
        <div
          className={hstack({
            pos: 'absolute',
            gap: 4,
            justifyContent: 'center',
            alignItems: 'center',
            w: '100%',
            py: 4,
            px: 24,
            bg: isUp ? 'content.state.positive' : 'content.state.negative',
          })}
        >
          <CheckCircleIcon size={12} className={css({ color: 'content.default.level-5' })} />
          <span className={css({ textStyle: 'Text/S/Strong', color: 'content.default.level-5' })}>Selected</span>
        </div>
        <div
          className={vstack({
            flex: 1,
            gap: 8,
            p: 8,
            alignItems: 'center',
            justifyContent: 'center',
            bg: bgColor,
            h: { base: 157, tablet: '100%' },
            roundedBottom: 14,
          })}
        >
          <VoteIcon isUp={isUp} />
          <VoteRate rate={rate} isUp={isUp} />
        </div>
      </div>
    );
  }

  // 투표 완료 후 비선택 상태
  if (isDisabledState) {
    return (
      <div
        className={vstack({
          flex: 1,
          gap: 8,
          p: 8,
          rounded: 16,
          bg: bgColor,
          alignItems: 'center',
          justifyContent: 'center',
          h: { base: 157, tablet: '100%' },
        })}
      >
        <VoteIcon isUp={isUp} />
        <VoteRate rate={rate} isUp={isUp} />
      </div>
    );
  }

  // 투표 전 (기본 상태)
  return (
    <button
      type='button'
      disabled={disabled}
      onClick={onClick}
      className={vstack({
        flex: 1,
        gap: 24,
        p: { base: 8, tablet: 24 },
        rounded: 16,
        bg: bgColor,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s',
        alignItems: 'center',
        justifyContent: 'center',
        _hover: { opacity: disabled ? undefined : 0.9 },
      })}
    >
      <div className={vstack({ gap: 8, alignItems: 'center', pt: 8 })}>
        <VoteIcon isUp={isUp} size={32} />
        <VoteRate rate={rate} isUp={isUp} />
      </div>
      <GlowButton isUp={isUp} />
    </button>
  );
};

const GlowButton = ({ isUp }: { isUp: boolean }) => {
  const color = token(isUp ? 'colors.content.state.positive' : 'colors.content.state.negative');

  return (
    <span
      className={center({
        pos: 'relative',
        w: '100%',
        h: 54,
        px: 12,
        rounded: 1000,
        color: 'inverse.white',
        textStyle: 'Monospace/Title/XS',
        overflow: 'hidden',
        '& > span': { zIndex: 10 },
        _before: {
          content: '""',
          pos: 'absolute',
          zIndex: 0,
          left: '50%',
          top: '50%',
          marginLeft: '-50%',
          marginTop: '-50%',
          w: '100%',
          aspectRatio: '1 / 1',
          bg: 'var(--vote-color)',
          bgImage: 'linear-gradient(var(--vote-color) 40%, #fff 90%, #fff)',
          bgRepeat: 'no-repeat',
          animation: 'buttonBorderRotate 2s linear infinite',
        },
        _after: {
          content: '""',
          pos: 'absolute',
          zIndex: 5,
          left: '2px',
          top: '2px',
          w: 'calc(100% - 4px)',
          h: 'calc(100% - 4px)',
          bg: 'var(--vote-color)',
          rounded: 1000,
        },
      })}
      style={
        {
          boxShadow: `0px 0px 20px 0px color-mix(in srgb, ${color} 60%, transparent)`,
          '--vote-color': color,
        } as React.CSSProperties
      }
    >
      <span className={css({ display: { base: 'none', tablet: 'inline' } })}>
        {isUp ? (
          <FormattedMessage id='Campaign.r260123.Going_up' />
        ) : (
          <FormattedMessage id='Campaign.r260123.Going_down' />
        )}
      </span>
      <span className={css({ display: { base: 'inline', tablet: 'none' } })}>
        {isUp ? (
          <FormattedMessage id='Campaign.r260123.Label_up' />
        ) : (
          <FormattedMessage id='Campaign.r260123.Label_down' />
        )}
      </span>
    </span>
  );
};

type TimerColorType = 'neutral' | 'warning' | 'negative';

const getTimerColor = (timeLeftMinutes: number): TimerColorType => {
  if (timeLeftMinutes < 60) return 'negative'; // 1시간 미만
  if (timeLeftMinutes < 240) return 'warning'; // 4시간 미만
  return 'neutral'; // 4시간 이상
};

const formatTimeLeft = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

const timerColorRecipe = cva({
  variants: {
    color: {
      neutral: { color: 'content.default.level-3' },
      warning: { color: 'content.state.warning' },
      negative: { color: 'content.state.negative' },
    },
  },
  defaultVariants: {
    color: 'neutral',
  },
});

const TimerBadge = ({ timeLeftMinutes, isUpcoming }: { timeLeftMinutes: number; isUpcoming: boolean }) => {
  if (isUpcoming) {
    return null;
  }

  if (timeLeftMinutes <= 0) {
    return (
      <Badge type='tint' color='neutral'>
        <FormattedMessage id='Campaign.r260123.Voting_ended' />
      </Badge>
    );
  }

  const colorType = getTimerColor(timeLeftMinutes);
  const timeDisplay = formatTimeLeft(timeLeftMinutes);

  return (
    <div
      className={hstack({
        gap: 4,
        alignItems: 'center',
        px: { base: 0, tablet: 8 },
        py: { base: 0, tablet: 4 },
        rounded: 6,
        bg: { base: 'transparent' },
      })}
    >
      <ClockIcon size={16} className={timerColorRecipe({ color: colorType })} />
      <span
        className={css(
          { textStyle: { base: 'Monospace/Text/L/Regular', tablet: 'Monospace/Text/S/Regular' } },
          timerColorRecipe.raw({ color: colorType }),
        )}
      >
        <FormattedMessage id='Campaign.r260123.time_left' values={{ time: timeDisplay }} />
      </span>
    </div>
  );
};
